import { useContext, useEffect, useState } from 'react';
import { useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { ContentContext } from '@parcel2go/npm-p2g-app';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Tagged } from '~/apps/smartsend/components/Tagged';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { NavigationContext } from '~/components/nav/nav-context';
import { P2GImageMessage } from '~/components/imagemessage';
import { P2GPagination } from '~/apps/smartsend/components/smartsend/P2GPagination';
import { useArchivedOrders } from '~/apps/archived-orders/use-archived-orders';
import { ArchivedOrdersTable } from '~/apps/archived-orders/ArchivedOrdersTable';
import useGoto from '~/hooks/use-goto';
import useOnLoad from '../use-on-load';

export default function Component() {
  const isXs = useP2GMediaQueryOnly('xs');
  const { goTo } = useGoto();
  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { t } = useTranslation(TranslationNamespaces.MYORDERS);
  const [take, setTake] = useState(25);
  const [skip, setSkip] = useState(0);

  const { orders, itemCount, isLoading, featureEnabled } = useArchivedOrders(take, skip);

  useOnLoad(() => {
    const title = t('ARCHIVED_ORDERS_PAGE_TITLE', { defaultValue: 'Archived Orders' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.MYORDERS });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: {
        title: title,
        currentPageRoute: RoutingConstants.ArchivedOrders,
        showSmartActions: false
      }
    });
  });

  useEffect(() => {
    if (!featureEnabled) {
      goTo(RoutingConstants.MyAccount);
    }
  }, [featureEnabled, goTo]);

  if (!featureEnabled) {
    return null;
  }

  const setPaging = (newSkip: number, newTake: number) => {
    setSkip(newSkip);
    setTake(newTake);
  };

  return (
    <Tagged tag="screen-archived-orders">
      {isLoading ? (
        <P2GImageMessage
          image="/theme/common/notifications/basket.png?v=1"
          contentKey="image-archived-orders-loading"
          message={t('ARCHIVED_ORDERS_LOADING_TITLE', { defaultValue: 'Loading your orders' })}
        />
      ) : orders.length === 0 ? (
        <P2GImageMessage
          image="/theme/common/notifications/basket.png?v=1"
          contentKey="image-archived-orders-empty"
          message={t('ARCHIVED_ORDERS_EMPTY_TITLE', { defaultValue: "You don't have any archived orders" })}
        />
      ) : (
        <P2GPagination isXs={isXs} itemCount={itemCount} skip={skip} take={take} setPaging={setPaging}>
          <ArchivedOrdersTable orders={orders} isXs={isXs} />
        </P2GPagination>
      )}
    </Tagged>
  );
}

Component.displayName = 'Archived Orders';
